/**
 * The URL query as the app reads it: one string per parameter, or every value in order when a
 * key repeats. Null is part of the type because callers write null to remove a key.
 */
export type ParsedQuery<T = string> = Record<string, T | null | Array<T | null>>;

type QueryInputValue = string | number | boolean | null | undefined;

/** What `stringifyQuery` accepts: a parameter's value, or several values for a repeated key. */
export type QueryInput = Record<string, QueryInputValue | QueryInputValue[]>;

/**
 * Reads a `location.search` string (with or without the leading `?`). Keys come back sorted, so
 * two URLs with the same parameters produce equal objects whatever their order. A key without a
 * value (`?a`) reads as an empty string.
 */
export function parseQuery(search: string): ParsedQuery {
    const parsed: Record<string, string | string[]> = {};
    for (const [key, value] of new URLSearchParams(search)) {
        const existing = parsed[key];
        if (existing === undefined) parsed[key] = value;
        else if (Array.isArray(existing)) existing.push(value);
        else parsed[key] = [existing, value];
    }

    const sorted: ParsedQuery = {};
    for (const key of Object.keys(parsed).sort()) {
        sorted[key] = parsed[key];
    }
    return sorted;
}

/**
 * Writes parameters as a search string without the leading `?`, keys sorted so the same
 * parameters always give the same URL. A repeated key is written once per value; null and
 * undefined values are left out.
 */
export function stringifyQuery(params: QueryInput): string {
    const search = new URLSearchParams();
    for (const key of Object.keys(params).sort()) {
        const value = params[key];
        const values = Array.isArray(value) ? value : [value];
        for (const item of values) {
            if (item !== null && item !== undefined) search.append(key, String(item));
        }
    }
    return search.toString();
}
